import { cartoRawTile } from "./mapTiles";

const W = 1080;
const H = 1080;

const GREEN = "#52B788";
const AMBER = "#F59E0B";
const TERRA = "#E07A5F";
const BG = "#0F172A";
const TILE_BG = "#1B263B";
const TEXT = "#F8FAFC";
const MUTED = "#94A3B8";

const loadImg = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const worldPx = (lat, lon, z) => {
  const s = 256 * 2 ** z;
  const x = ((lon + 180) / 360) * s;
  const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * s;
  return [x, y];
};

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const havKm = (a, b) => {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const drawTrophy = (ctx, cx, cy, size, color) => {
  const s = size / 24;
  ctx.save();
  ctx.translate(cx - 12 * s, cy - 12 * s);
  ctx.scale(s, s);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(6, 9); ctx.lineTo(4.5, 9);
  ctx.arc(4.5, 6.5, 2.5, Math.PI / 2, -Math.PI / 2, false); ctx.lineTo(6, 4); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(18, 9); ctx.lineTo(19.5, 9);
  ctx.arc(19.5, 6.5, 2.5, Math.PI / 2, -Math.PI / 2, true); ctx.lineTo(18, 4); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(6, 3); ctx.lineTo(18, 3); ctx.lineTo(18, 10);
  ctx.arc(12, 10, 6, 0, Math.PI, false); ctx.lineTo(6, 3); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(12, 16); ctx.lineTo(12, 19); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(8, 21); ctx.lineTo(16, 21); ctx.stroke();
  ctx.restore();
};

const dayGeometry = (trip, day) => {
  const raw = trip.route?.geometry || [];
  if (raw.length < 2) return { seg: [], all: [] };
  const cum = [0];
  for (let i = 1; i < raw.length; i++) cum.push(cum[i - 1] + havKm(raw[i - 1], raw[i]));
  const geomTotal = cum[cum.length - 1];
  const driveDays = (trip.days || []).filter((d) => d.type !== "stay");
  const routeTotal = driveDays.reduce((a, d) => a + (d.distance_km || 0), 0) || geomTotal;
  const f = routeTotal > 0 ? geomTotal / routeTotal : 1;
  let startKm = day.route_start_km;
  if (startKm == null) startKm = driveDays.filter((d) => d.day_number < day.day_number).reduce((a, d) => a + (d.distance_km || 0), 0);
  let endKm = day.route_end_km;
  if (endKm == null) endKm = startKm + (day.distance_km || 0);
  const s = startKm * f, e = endKm * f;
  const seg = [];
  for (let i = 0; i < raw.length; i++) if (cum[i] >= s - 0.5 && cum[i] <= e + 0.5) seg.push(raw[i]);
  return { seg: seg.length >= 2 ? seg : [], all: raw };
};

export async function generateStageCard(trip, day) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  const MX = 54;
  ctx.textAlign = "left";
  drawTrophy(ctx, MX + 24, 78, 46, AMBER);
  ctx.font = "800 30px 'JetBrains Mono', monospace";
  ctx.fillStyle = AMBER;
  ctx.fillText("LEUKSTE ETAPPE", MX + 62, 88);
  ctx.font = "700 52px Figtree, sans-serif";
  ctx.fillStyle = TEXT;
  let title = `${day.start?.name || ""} → ${day.end?.name || ""}`;
  while (ctx.measureText(title).width > W - 2 * MX && title.length > 8) title = `${title.slice(0, -2)}…`;
  ctx.fillText(title, MX, 160);
  ctx.font = "500 27px 'JetBrains Mono', monospace";
  ctx.fillStyle = MUTED;
  const dateStr = day.date ? new Date(`${day.date}T12:00:00`).toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long" }) : "";
  ctx.fillText(`Dag ${day.day_number} · ${dateStr} · ${trip.name || ""}`.trim(), MX, 206);

  const mapTop = 244;
  const mapH = 620;
  const mapX = MX;
  const mapW = W - 2 * MX;
  ctx.save();
  rr(ctx, mapX, mapTop, mapW, mapH, 32);
  ctx.clip();
  ctx.fillStyle = TILE_BG;
  ctx.fillRect(mapX, mapTop, mapW, mapH);

  const { seg, all } = dayGeometry(trip, day);
  const focus = seg.length >= 2 ? seg : all;
  if (focus.length >= 2) {
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    for (const [la, lo] of focus) {
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
      if (lo < minLon) minLon = lo;
      if (lo > maxLon) maxLon = lo;
    }
    let zoom = 5;
    for (let z = 13; z >= 3; z--) {
      const [x1, y1] = worldPx(maxLat, minLon, z);
      const [x2, y2] = worldPx(minLat, maxLon, z);
      if (x2 - x1 <= mapW - 130 && y2 - y1 <= mapH - 130) { zoom = z; break; }
    }
    const [bx1, by1] = worldPx(maxLat, minLon, zoom);
    const [bx2, by2] = worldPx(minLat, maxLon, zoom);
    const cx = (bx1 + bx2) / 2, cy = (by1 + by2) / 2;
    const tlx = cx - mapW / 2, tly = cy - mapH / 2;
    const maxTile = 2 ** zoom;
    const tiles = [];
    for (let tx = Math.floor(tlx / 256); tx <= Math.floor((tlx + mapW) / 256); tx++) {
      for (let ty = Math.floor(tly / 256); ty <= Math.floor((tly + mapH) / 256); ty++) {
        if (ty < 0 || ty >= maxTile) continue;
        const wx = ((tx % maxTile) + maxTile) % maxTile;
        tiles.push({ tx, ty, url: cartoRawTile("dark_all", zoom, wx, ty, "abcd"[(tx + ty) % 4]) });
      }
    }
    const imgs = await Promise.all(tiles.map((t) => loadImg(t.url)));
    tiles.forEach((t, i) => { if (imgs[i]) ctx.drawImage(imgs[i], mapX + t.tx * 256 - tlx, mapTop + t.ty * 256 - tly, 256, 256); });

    const projPts = (pts) => pts.map(([la, lo]) => { const [x, y] = worldPx(la, lo, zoom); return [mapX + x - tlx, mapTop + y - tly]; });
    const path = (pts) => { ctx.beginPath(); pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y))); };
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    if (seg.length >= 2 && all.length >= 2) {
      const thin = Math.max(1, Math.ceil(all.length / 900));
      path(projPts(all.filter((_, i) => i % thin === 0)));
      ctx.strokeStyle = "rgba(148,163,184,0.35)";
      ctx.lineWidth = 4;
      ctx.stroke();
    }
    const segPts = projPts(focus);
    path(segPts);
    ctx.strokeStyle = "rgba(15,23,42,0.85)";
    ctx.lineWidth = 14;
    ctx.stroke();
    path(segPts);
    ctx.save();
    ctx.shadowColor = "rgba(245,158,11,0.7)";
    ctx.shadowBlur = 18;
    ctx.strokeStyle = AMBER;
    ctx.lineWidth = 9;
    ctx.stroke();
    ctx.restore();

    const dot = ([x, y], ring) => {
      ctx.beginPath(); ctx.arc(x, y, 13, 0, Math.PI * 2); ctx.fillStyle = "#FFFFFF"; ctx.fill();
      ctx.lineWidth = 5; ctx.strokeStyle = ring; ctx.stroke();
    };
    dot(segPts[0], GREEN);
    dot(segPts[segPts.length - 1], TERRA);
  }
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.textAlign = "right";
  ctx.fillText("© OpenStreetMap © CARTO", mapX + mapW - 14, mapTop + mapH - 14);
  ctx.restore();
  rr(ctx, mapX, mapTop, mapW, mapH, 32);
  ctx.strokeStyle = "rgba(245,158,11,0.5)";
  ctx.lineWidth = 3;
  ctx.stroke();

  const statsY = mapTop + mapH + 66;
  ctx.textAlign = "left";
  const stats = [];
  if (day.distance_km) stats.push(`${Math.round(day.distance_km)} km`);
  if (day.drive_time_hours) stats.push(`${Math.floor(day.drive_time_hours)}u${String(Math.round((day.drive_time_hours % 1) * 60)).padStart(2, "0")} rijden`);
  if (day.curviness != null) stats.push(`bochtenscore ${Math.round(day.curviness)}`);
  ctx.font = "700 40px Figtree, sans-serif";
  ctx.fillStyle = TEXT;
  ctx.fillText(stats.join("   ·   "), MX, statsY);

  if (day.curve_stars) {
    ctx.font = "700 44px Figtree, sans-serif";
    let sx = MX;
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = i < day.curve_stars ? AMBER : "rgba(148,163,184,0.3)";
      ctx.fillText("★", sx, statsY + 58);
      sx += 48;
    }
    ctx.font = "500 25px 'JetBrains Mono', monospace";
    ctx.fillStyle = MUTED;
    ctx.fillText("bochtig genot", sx + 14, statsY + 52);
  }

  ctx.font = "700 32px Figtree, sans-serif";
  ctx.fillStyle = GREEN;
  ctx.fillText("VoltRoute", MX, H - 44);
  ctx.font = "500 24px 'JetBrains Mono', monospace";
  ctx.fillStyle = MUTED;
  ctx.textAlign = "right";
  ctx.fillText("gepland met voltroute.org", W - MX, H - 44);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export async function shareStageCard(trip, day) {
  const blob = await generateStageCard(trip, day);
  if (!blob) throw new Error("kaart genereren mislukt");
  const file = new File([blob], `leukste-etappe-dag-${day.day_number}.png`, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Leukste etappe", text: `De leukste etappe van onze trip: ${day.start?.name} → ${day.end?.name} 🏍` });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return "downloaded";
}
